import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { PageContentApiP1Response } from "../../../../api/services/pageContent/models";
import { SessionsAndEventsResponse } from "../../../../api/services/program/models";
import {
  ITabs,
  MuiTabPanel,
  MuiTabs,
  resolveContent,
} from "../../../../components";
import {
  PAGE_IDS,
  SOLUTION_OFFERING_CMC_SITECORE_KEY,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../helpers/constants";
import { getValueOrBlank } from "../../../../helpers/utils";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { DocumentsTab } from "../../../createProgram/components/createProgramTabs/tabs/documents/DocumentsTab";
import {
  ParticipantDetail,
  ProgramDetail,
  ProgramStatusList,
} from "../../../createProgram/modelsAndSchemas/Models";
import { programDocumentsTabSiteCoreKeys } from "../../../createProgram/siteCore/SiteCoreKeys";
import { ParticipantTab } from "../../../viewProgram/components/programAndParticipant/tabs/participant/ParticipantTab";
import { ProgramTab } from "../../../viewProgram/components/programAndParticipant/tabs/participant/ProgramTab";
import { CPParticipantActivityTimeline } from "../../components/cpParticipantActivityTimeline/CPParticipantActivityTimeline";
import { ManageParticipantsSessionAndEventsTab } from "../../components/manageParticipantsSessionAndEventsTab/ManageParticipantsSessionAndEventsTab";
import {
  CATEGORY_FILTER_KEYS,
  ManageParticipantsSessionAndEventsFilterFormKeys,
  TYPE_FILTER_KEYS,
  UPCOMING_PERIOD_FILTER_KEYS,
} from "../../modelsAndSchemas/Models";
import { manageParticipantHeaderSiteCoreKeys } from "../../sitecore/SiteCoreKeys";
import { CPViewParticipantWrapper } from "./CPViewParticipant.style";

const pageId = PAGE_IDS.COACH_PORTAL_VIEW_PARTICIPANT;

interface CPViewParticipantProps {
  programDetail?: ProgramDetail;
  participantDetail?: ParticipantDetail;
  sessionsEventList?: SessionsAndEventsResponse[];
  programStatusList: ProgramStatusList[];
  pageContentData?: PageContentApiP1Response;
  setSelectedTabIndex?: (index: number) => void;
  getProgramStatusListApi: () => void;
  selectedTabIndex: number;
}

export const CPViewParticipant = ({
  programDetail,
  participantDetail,
  sessionsEventList,
  programStatusList,
  pageContentData,
  setSelectedTabIndex,
  getProgramStatusListApi,
  selectedTabIndex,
}: CPViewParticipantProps) => {
  const { id: programId = "" } = useParams();
  const { useFetchLabel, fetchComponentTitle } = useSiteCore();

  const sessionsAndEventsFormDefaultValues = {
    [ManageParticipantsSessionAndEventsFilterFormKeys.type]:
      TYPE_FILTER_KEYS.UPCOMING_SESSIONS_AND_EVENTS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.period]:
      UPCOMING_PERIOD_FILTER_KEYS.NEXT_7_DAYS,
    [ManageParticipantsSessionAndEventsFilterFormKeys.category]:
      CATEGORY_FILTER_KEYS.ALL,
  };

  const programAndParticipantMethods = useForm({
    mode: "all",
  });

  const methods = useForm({
    mode: "all",
    defaultValues: sessionsAndEventsFormDefaultValues,
  });

  const {
    manageParticipantHeaderTitle,
    programTab,
    participantTab,
    activityTimelineTab,
    sessionsAndEventsTab,
    documentsTab,
  } = manageParticipantHeaderSiteCoreKeys;

  const getLabel = useFetchLabel(manageParticipantHeaderTitle);

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTabIndex?.(value);
  };

  const isCMCProgram = useMemo(() => {
    return (
      programDetail?.solutionOfferingSiteCoreKey ===
      SOLUTION_OFFERING_CMC_SITECORE_KEY
    );
  }, [programDetail]);

  const isTMAssessmentProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT;
  }, [programDetail?.tmProgramType]);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_LEADER_COACHING;
  }, [programDetail?.tmProgramType]);

  const renderProgramTabContent = useMemo(
    () => (
      <FormProvider {...programAndParticipantMethods}>
        <ProgramTab
          pageId={pageId}
          programDetail={programDetail}
          programStatusList={programStatusList}
          refetchProgramStatusList={getProgramStatusListApi}
          isCMCProgram={isCMCProgram}
          isTMAssessmentProgram={isTMAssessmentProgram}
          isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
          isFromCoachPortal
        />
      </FormProvider>
    ),
    [participantDetail?.projectedProgramEndDate, programDetail],
  );

  const renderParticipantsInformationTab = useMemo(
    () => (
      <FormProvider {...programAndParticipantMethods}>
        <ParticipantTab
          pageId={pageId}
          programDetail={programDetail}
          programId={programId}
          participantDetail={participantDetail}
          pageContentData={pageContentData}
          nextPersona={programDetail?.nextPersona}
          isCMCProgram={isCMCProgram}
          isTMAssessmentProgram={isTMAssessmentProgram}
          isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
          deliveryCountry={getValueOrBlank(programDetail?.deliveryCountry)}
          isFromCoachPortal
        />
      </FormProvider>
    ),
    [participantDetail, programDetail, pageContentData],
  );

  const renderDocumentTabContent = () => (
    <DocumentsTab
      pageId={pageId}
      programId={programId}
      isViewOnly
      isFromCoachPortal
    />
  );

  const renderSessionAndEventsTabContent = useMemo(
    () => (
      <FormProvider {...methods}>
        <ManageParticipantsSessionAndEventsTab
          sessionsEventList={sessionsEventList}
        />
      </FormProvider>
    ),
    [sessionsEventList],
  );

  const tabIdProgramTabView = `${getLabel(programTab)}-tab`;
  const tabIdParticipantTabView = `${getLabel(participantTab)}-tab`;
  const tabIdDocumentsView = `${getLabel(documentsTab)}-tab`;
  const tabIdActivityTimelineTabView = `${getLabel(activityTimelineTab)}-tab`;
  const tabIdSessionAndEventsTabView = `${getLabel(sessionsAndEventsTab)}-tab`;

  const participantActivityTimelineMethods = useForm({
    mode: "all",
  });

  const VIEW_PARTICIPANTS_TABS: ITabs[] = [
    {
      id: tabIdProgramTabView,
      label: getLabel(programTab),
      content: () => renderProgramTabContent,
    },
    {
      id: tabIdParticipantTabView,
      label: getLabel(participantTab),
      content: () => renderParticipantsInformationTab,
    },
    {
      id: tabIdDocumentsView,
      label: fetchComponentTitle(
        programDocumentsTabSiteCoreKeys.documentsTitle,
      ),
      content: () => renderDocumentTabContent(),
    },
    {
      id: tabIdActivityTimelineTabView,
      label: getLabel(activityTimelineTab),
      content: (
        <CPParticipantActivityTimeline
          programId={programId}
          participantDetail={participantDetail}
          participantActivityTimelineMethods={
            participantActivityTimelineMethods
          }
        />
      ),
    },
    {
      id: tabIdSessionAndEventsTabView,
      label: getLabel(sessionsAndEventsTab),
      content: () => renderSessionAndEventsTabContent,
    },
  ];

  const currentTabContent = resolveContent(
    VIEW_PARTICIPANTS_TABS[selectedTabIndex]?.content,
  );

  return (
    <CPViewParticipantWrapper data-testid="cp-view-participant-main-wrapper">
      <MuiTabs
        tabs={VIEW_PARTICIPANTS_TABS}
        value={selectedTabIndex}
        onChange={onTabChange}
        tabsTestIds={VIEW_PARTICIPANTS_TABS.map(
          (_, index) => `${pageId}-tab${index + 1}`,
        )}
        className="program-participant-tab"
        data-testid="cp-view-participant-tabs"
      >
        <MuiTabPanel
          panelId={`${pageId}-tab${selectedTabIndex + 1}`}
          value={selectedTabIndex}
          index={selectedTabIndex}
          isPanelWithMount
          ariaLabelledById={VIEW_PARTICIPANTS_TABS[selectedTabIndex]?.id}
          data-testid={`cp-view-participant-tab-panel-${selectedTabIndex}`}
        >
          {currentTabContent}
        </MuiTabPanel>
      </MuiTabs>
    </CPViewParticipantWrapper>
  );
};
